import { Link } from "react-router-dom";
import { ArrowRight, Package, MessageSquare, CornerUpRight, Mail, Share2, Users } from "lucide-react";
import { GROWING, LOGOS } from "@/data";
import { Reveal, SectionHead, ArrowLink, BrandLogo } from "@/components/primitives";
import { DemoLink } from "@/components/Booking";
import { TrialLink, TRIAL_NOTE } from "@/components/marketing/Shared";

export const PricingStrip = () => (
  <section className="relative px-4 sm:px-6 -mt-6 lg:-mt-10 pb-6" data-testid="pricing-strip">
    <Reveal className="mx-auto max-w-7xl">
      <Link to="/pricing" className="group glass card-lift rounded-full flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 px-6 py-3.5 text-sm text-[var(--qd-ink)]" data-testid="pricing-strip-link">
        <span className="inline-flex items-center gap-2 font-semibold"><span className="live-dot" /> <span className="text-[var(--qd-accent)]">$0.79</span> per resolution</span>
        <span className="hidden sm:inline text-[var(--qd-muted)]">·</span>
        <span className="text-[var(--qd-muted)]">credits carry forward</span>
        <span className="hidden sm:inline text-[var(--qd-muted)]">·</span>
        <span className="text-[var(--qd-muted)]">no seat costs</span>
        <span className="link-arrow text-sm ml-0 sm:ml-2">See pricing <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" /></span>
      </Link>
    </Reveal>
  </section>
);

export const Growing = () => (
  <section id="why-us" className="relative py-24 lg:py-32" data-testid="section-growing">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <SectionHead className="lg:col-span-7" eyebrow="For teams that are growing" title={<>The support desk you start on and <em className="italic font-light text-[var(--qd-accent)]">don't outgrow.</em></>} />
        <Reveal className="lg:col-span-5 lg:pb-2" delay={0.15}>
          <p className="text-[var(--qd-muted)] leading-relaxed">Every help desk assumes you already have a support team, and prices, onboards and licenses you like you do. Quantum Desk is built for the team you are now — and stays with you as you grow.</p>
        </Reveal>
      </div>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {GROWING.map((g, i) => (
          <Reveal key={g.num} delay={i * 0.1} className="h-full">
            <article className="glass card-lift rounded-3xl p-6 h-full flex flex-col" data-testid={`growing-card-${g.num}`}>
              <span className="font-display text-4xl text-[var(--qd-accent)]">{g.num}</span>
              <h3 className="font-display text-xl mt-6 leading-snug">{g.title}</h3>
              <p className="text-sm text-[var(--qd-muted)] mt-3 leading-relaxed flex-1">{g.desc}</p>
              <ArrowLink href={i === 3 ? "#d2c" : "/pricing"} className="mt-6" testId={`growing-card-${g.num}-cta`}>{g.cta}</ArrowLink>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Chip = ({ name }) => (
  <span className="inline-flex items-center gap-2 rounded-full bg-white border border-[var(--qd-line)] pl-2 pr-3 py-1.5 text-xs font-semibold" data-testid={`chip-${name.toLowerCase()}`}>
    <BrandLogo src={LOGOS[name]} name={name} size={16} /> {name}
  </span>
);

const Chips = ({ names }) => (
  <Reveal delay={0.1} className="mt-8">
    <p className="eyebrow mb-3">Built around</p>
    <div className="flex flex-wrap gap-2">{names.map((t) => <Chip key={t} name={t} />)}</div>
  </Reveal>
);

const StepCard = ({ icon: Icon, title, text, index, testId }) => (
  <Reveal delay={0.1 + index * 0.08}>
    <article className="glass card-lift rounded-3xl p-6 flex gap-5 items-start" data-testid={testId}>
      <span className="shrink-0 w-11 h-11 rounded-2xl bg-[var(--qd-tint)] text-[var(--qd-accent)] flex items-center justify-center"><Icon className="w-5 h-5" /></span>
      <div>
        <h3 className="font-display text-xl leading-snug">{title}</h3>
        <p className="text-sm text-[var(--qd-muted)] mt-2 leading-relaxed">{text}</p>
      </div>
    </article>
  </Reveal>
);

const B2B_STEPS = [
  [Mail, "Email and Slack, one workflow", "Gmail, Outlook and Slack conversations land in a single AI-routed desk, with Jira a step away for bugs."],
  [Users, "Specialists step in with context", "Finance, AMs and engineers join a conversation with the full customer history, not a forwarded thread."],
  [Share2, "Hand-offs that carry everything", "Every escalation keeps the context, the promises made and the next step, so customers never repeat themselves."],
];

const D2C_STEPS = [
  [Package, "Order support, connected", "Bring order details into the conversation, so customers don’t have to start over."],
  [MessageSquare, "A resolution across channels", "Handle routine requests against your store’s policies, from email to messaging."],
  [CornerUpRight, "People where they matter", "Hand off exceptions with the customer history and next steps in context."],
];

export const B2B = () => (
  <section id="b2b" className="relative py-24 lg:py-32" data-testid="section-b2b">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-6">
        <SectionHead eyebrow="For B2B teams & service businesses" title={<>Complex conversations. <em className="italic font-light text-[var(--qd-accent)]">Connected support.</em></>} desc="Keep customers, support teams and specialists on the same page. Bring email and Slack into one workflow, and carry the context through every hand-off." />
        <Chips names={["Gmail", "Outlook", "Slack", "Jira", "HubSpot"]} />
        <Reveal delay={0.15} className="mt-8">
          <div className="flex flex-wrap items-center gap-5">
            <TrialLink className="btn-primary" testId="b2b-trial">Try free for two months <ArrowRight className="w-4 h-4" /></TrialLink>
            <DemoLink ctx={{ source: "b2b" }} className="link-arrow text-sm" testId="b2b-cta" />
          </div>
          <p className="text-xs text-[var(--qd-muted)] mt-4" data-testid="b2b-trial-terms">{TRIAL_NOTE}</p>
        </Reveal>
      </div>
      <div className="lg:col-span-6 flex flex-col gap-4">
        {B2B_STEPS.map(([icon, title, text], i) => <StepCard key={title} icon={icon} title={title} text={text} index={i} testId={`b2b-step-${i}`} />)}
        <Reveal delay={0.35}>
          <div className="rounded-3xl bg-[var(--qd-accent)] text-white p-6 sm:p-7 grain relative overflow-hidden" data-testid="b2b-resolution-first">
            <p className="eyebrow !text-white/70">Resolution pricing comes first</p>
            <p className="font-display text-2xl sm:text-3xl mt-2">Start at $0.79 per prepaid resolution.</p>
            <p className="text-sm text-white/75 mt-2">Forecast your usage. Carry unused credits forward. Extra resolutions cost $0.99 each.</p>
            <div className="mt-5 flex flex-wrap items-center gap-5">
              <Link to="/pricing" className="btn-primary !bg-white !text-[var(--qd-accent)]" data-testid="b2b-resolution-pricing">See resolution pricing <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/pricing#seat-plans" className="text-sm font-semibold text-white/85 hover:text-white transition-colors" data-testid="b2b-seat-jump">Or explore B2B-only seat plans →</Link>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const D2C = () => (
  <section id="d2c" className="relative py-24 lg:py-32" data-testid="section-d2c">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-6 lg:order-2">
        <SectionHead eyebrow="For D2C startups & growing brands" title={<>You grow the business. <em className="italic font-light text-[var(--qd-accent)]">We’ll handle your support operations.</em></>} desc="Order questions, delivery updates, refunds and replacements — brought together across your store, email and messaging channels. Don’t buy seats anymore. Pay for tickets that reach resolution." />
        <Chips names={["Shopify", "WooCommerce", "WhatsApp", "TikTok", "Telegram"]} />
        <Reveal delay={0.15} className="mt-8">
          <div className="flex flex-wrap items-center gap-5">
            <TrialLink className="btn-primary" testId="d2c-trial">Try free for two months <ArrowRight className="w-4 h-4" /></TrialLink>
            <Link to="/pricing" className="link-arrow text-sm" data-testid="d2c-pricing-link">Resolutions from $0.79 <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <p className="text-xs text-[var(--qd-muted)] mt-4" data-testid="d2c-trial-terms">{TRIAL_NOTE}</p>
        </Reveal>
      </div>
      <div className="lg:col-span-6 lg:order-1 flex flex-col gap-4">
        {D2C_STEPS.map(([icon, title, text], i) => <StepCard key={title} icon={icon} title={title} text={text} index={i} testId={`d2c-operation-${i}`} />)}
        <Reveal delay={0.35}>
          <div className="glass rounded-3xl p-6 sm:p-7" data-testid="d2c-pricing-note">
            <p className="eyebrow mb-2">Make growth about customers, not licenses</p>
            <p className="font-display text-2xl">Buy your forecast. Keep your unused credits.</p>
            <p className="text-sm text-[var(--qd-muted)] mt-2">Prebuy resolutions at $0.79 based on the volume you expect. Unused credits carry into the next period; anything beyond is $0.99 a resolution.</p>
            <Link to="/pricing#calculator" className="link-arrow text-sm mt-4" data-testid="d2c-savings-link">Compare against your current seat bill <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
